import React, { useState, useEffect } from "react";
import Modal from "react-modal";
import { orderApiService } from "../../../../service/admin/ordersApiService";
import { useLoader } from "../../../../base/context/LoaderProvider";
import { ToastTypes, useMessage } from "../../../../base/context/MessageProvider";
import "./style.css";

const modalStyles = {
  content: {
    top: "50%",
    left: "50%",
    right: "auto",
    bottom: "auto",
    marginRight: "-50%",
    transform: "translate(-50%, -50%)",
    maxWidth: "420px",
    width: "90%",
    padding: "0",
    borderRadius: "12px",
    border: "none",
    boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.25)",
  },
  overlay: {
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 1000,
  },
};

Modal.setAppElement("#root");

const RefundModal = ({ isOpen, onRequestClose, orderData, onSuccess, variant }) => {
  const loader = useLoader();
  const message = useMessage();
  
  const [inputType, setInputType] = useState("amount");
  const [amount, setAmount] = useState("");
  const [percentage, setPercentage] = useState("");
  const [calculatedRefund, setCalculatedRefund] = useState(0);
  const [reason, setReason] = useState("");
  const [refundStatus, setRefundStatus] = useState("");
  const [images, setImages] = useState([]);

  // Auto-populate order amount when modal opens
  useEffect(() => {
    if (isOpen && orderData) {
      setAmount(orderData.totalAmount?.toString() || "");
      setPercentage("100");
      setCalculatedRefund(orderData.totalAmount || 0);
      setRefundStatus("full-refund");
    }
  }, [isOpen, orderData]);

  // Auto-calculate refund amount and auto-select refund type
  useEffect(() => {
    if (!orderData?.totalAmount) return;
    
    let refundAmount = 0;
    if (inputType === "amount") {
      refundAmount = Math.min(parseFloat(amount) || 0, orderData.totalAmount);
    } else if (inputType === "percentage") {
      const percentValue = Math.min(parseFloat(percentage) || 0, 100);
      refundAmount = (orderData.totalAmount * percentValue) / 100;
    }
    
    setCalculatedRefund(refundAmount);
    
    // Auto-select refund type based on amount
    if (refundAmount > 0) {
      if (Math.abs(refundAmount - orderData.totalAmount) < 0.01) {
        setRefundStatus("full-refund");
      } else {
        setRefundStatus("partially-refund");
      }
    }
  }, [inputType, amount, percentage, orderData?.totalAmount]);

  const formatCurrency = (value) => {
    const num = Number(value);
    if (!Number.isFinite(num)) return "₹0.00";
    return `₹${num.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const handleImageUpload = (event) => {
    const files = Array.from(event.target.files);
    setImages((prev) => [...prev, ...files]);
  };

  const removeImage = (index) => {
    setImages((prev) => prev.filter((_, i) => i !== index));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    if (calculatedRefund <= 0) {
      message.showToast("Please enter a valid refund amount", ToastTypes.ERROR);
      return;
    }

    if (!reason.trim()) {
      message.showToast("Please provide a reason", ToastTypes.ERROR);
      return;
    }

    const formData = new FormData();
    formData.append("refundedAmont", Math.round(calculatedRefund).toString());
    // Note: We always send the calculated amount, not the percentage
    formData.append("reason", reason);
    formData.append("refundStatus", refundStatus);
    formData.append("sellerId", orderData?.sellerId?._id || orderData?.sellerId);
    // Only send ticketId when present (ticket flow); for order rejection we send refundSource so backend can allow refund without ticket)
    if (orderData?.ticketId) {
      formData.append("ticketId", orderData.ticketId);
    }
    if (variant === "rejectOrder") {
      formData.append("refundSource", "order_rejection");
    }

    images.forEach((image) => {
      formData.append("images", image);
    });

    loader.showLoading(true);
    try {
      const res = await orderApiService.addRefund(formData, orderData.orderId);
      loader.showLoading(false);
      
      if (res.status.code === 200) {
        message.showToast("Refund credited to seller wallet", ToastTypes.SUCCESS);
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        message.showToast(res.error?.message || "Failed to process refund", ToastTypes.ERROR);
      }
    } catch (err) {
      loader.showLoading(false);
      message.showToast(err?.message || "Server error. Try again later", ToastTypes.ERROR);
    }
  };

  const resetForm = () => {
    setAmount("");
    setPercentage("");
    setCalculatedRefund(0);
    setReason("");
    setRefundStatus("");
    setImages([]);
    setInputType("amount");
  };

  const handleClose = () => {
    resetForm();
    onRequestClose();
  };

  // Always render Modal when isOpen so overlay appears; show content only when orderData is ready
  return (
    <Modal isOpen={isOpen} onRequestClose={handleClose} style={modalStyles} contentLabel="Refund Modal">
      {orderData ? (
      <div className="refund-modal-compact">
        {/* Header - same "Process Refund" for both flows */}
        <div className="modal-header">
          <h2>Process Refund</h2>
          <button type="button" className="close-btn" onClick={handleClose} aria-label="Close">×</button>
        </div>

        {/* Order Info: Order ID + Total Amount only */}
        <div className="order-info-compact">
          <div className="info-item">
            <span>Order:</span>
            <strong>{orderData.upsaleoOrderId || orderData.orderId}</strong>
          </div>
          <div className="info-item highlight">
            <span>Total Amount:</span>
            <strong>{formatCurrency(orderData.totalAmount)}</strong>
          </div>
        </div>

        {/* Form */}
        <form className="refund-form-compact" onSubmit={handleSubmit}>
          {/* Input Type Toggle */}
          <div className="toggle-row">
            <button type="button" className={`toggle-btn ${inputType === 'amount' ? 'active' : ''}`} onClick={() => setInputType("amount")}>
              Amount
            </button>
            <button type="button" className={`toggle-btn ${inputType === 'percentage' ? 'active' : ''}`} onClick={() => setInputType("percentage")}>
              Percentage
            </button>
          </div>

          {/* Input Field */}
          <div className="input-row">
            {inputType === "amount" ? (
              <div className="input-field">
                <span className="prefix">₹</span>
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="Enter amount"
                  max={orderData.totalAmount}
                  min="0"
                  step="0.01"
                />
              </div>
            ) : (
              <div className="input-field">
                <input
                  type="number"
                  value={percentage}
                  onChange={(e) => setPercentage(e.target.value)}
                  placeholder="Enter %"
                  max="100"
                  min="0"
                />
                <span className="suffix">%</span>
              </div>
            )}
          </div>

          {/* Calculated Amount */}
          <div className="refund-amount-display">
            <span>Refund:</span>
            <span className="amount">{formatCurrency(calculatedRefund)}</span>
            <span className={`type-badge ${refundStatus === 'full-refund' ? 'full' : 'partial'}`}>
              {refundStatus === 'full-refund' ? 'Full' : 'Partial'}
            </span>
          </div>

          {/* Reason */}
          <div className="form-field">
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Reason for refund..."
              rows={2}
              required
            />
          </div>

          {/* Image Upload - Compact */}
          <div className="form-field">
            <div className="upload-row">
              <input type="file" multiple onChange={handleImageUpload} accept="image/*" id="refund-img" hidden />
              <label htmlFor="refund-img" className="upload-label">+ Images</label>
              {images.length > 0 && <span className="file-count">{images.length} file(s)</span>}
            </div>
          </div>

          {/* Actions */}
          <div className="action-row">
            <button type="button" className="btn-cancel" onClick={handleClose}>Cancel</button>
            <button type="submit" className="btn-submit">Process Refund</button>
          </div>
        </form>
      </div>
      ) : (
        <div className="refund-modal-compact" style={{ padding: '24px', textAlign: 'center' }}>
          <p>Loading order details...</p>
        </div>
      )}
    </Modal>
  );
};

export default RefundModal;
